import axios, { AxiosInstance, AxiosResponse } from "axios";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import config from "../config/env";
import {
  BatchStatus,
  getBatchStatusDisplayText,
  getBatchStatusClass,
} from "../types/batchStatus";
import { tokenRefreshService } from "./tokenRefreshService";

export interface BatchExecutionItemDetail {
  mappingId: number | null;
  status: string | null;
  errorMessage: string | null;
}

export interface BatchDetailsItem {
  documentId: number | null;
  documentNumber: string | null;
  requestGroup: string | null;
  requestUser: string | null;
  requestDate: string | null;
  formName: string | null;
  status: string | null;
  items: BatchExecutionItemDetail[];
}

export interface BatchDetailsItemApi {
  documentId: number | null;
  documentNumber: string | null;
  requestGroup: string | null;
  requestUser: string | null;
  requestDate: string | null;
  formName: string | null;
  status: string | null;
  items: BatchExecutionItemDetail[];
}

export interface BatchDetailsResponse {
  success: boolean;
  data: BatchDetailsItem[];
  pagination: {
    page: number;
    perPage: number;
    total: string;
    totalPages: number;
  };
  filters: {
    formNames: string[];
    requestGroups: string[];
  };
  message: string;
}

export interface BatchDetailsApiResponse {
  success: boolean;
  data: BatchDetailsItemApi[];
  pagination: {
    page: number;
    perPage: number;
    total: string;
    totalPages: number;
  };
  filters: {
    formNames: string[];
    requestGroups: string[];
  };
  message: string;
}

export interface BatchDetailsParams {
  batchId: number;
  startDate?: string;
  endDate?: string;
  formName?: string;
  requestGroup?: string;
  status?: BatchStatus;
  page?: number;
  perPage?: number;
}

class BatchHistoryDetailService {
  private client: AxiosInstance;

  constructor() {
    const baseURL = config.apiUrl;

    this.client = axios.create({
      baseURL: `${baseURL}`,
      timeout: 15000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      config => {
        const token = Cookies.get("access_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        console.error("Batch history detail request error:", error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      response => response,
      error => {
        console.error(
          "Batch history detail response error:",
          error.response?.data || error.message
        );

        // Handle timeout errors
        if (
          error.code === "ECONNABORTED" ||
          error.message.includes("timeout")
        ) {
          toast.error(
            "リクエストがタイムアウトしました。しばらく待ってから再試行してください。"
          );
          return Promise.reject({
            success: false,
            message:
              "リクエストがタイムアウトしました。しばらく待ってから再試行してください。",
            errors: [
              {
                field: "general",
                message:
                  "ネットワーク接続が遅いか、サーバーが応答していません。",
              },
            ],
            isTimeout: true,
          });
        }

        // Don't handle 401 errors - let token refresh interceptor handle them
        if (error.response?.status === 401) {
          return Promise.reject(error);
        }

        return Promise.reject(error);
      }
    );

    // Setup automatic token refresh
    tokenRefreshService.setupAxiosInterceptor(this.client);
  }

  /**
   * Map API status string to BatchStatus enum
   */
  private mapApiStatusToBatchStatus(apiStatus: string): BatchStatus {
    switch (apiStatus?.toUpperCase()) {
      case "SUCCESS":
        return BatchStatus.SUCCESS;
      case "ERROR":
      case "FAILED":
        return BatchStatus.FAILED;
      case "PENDING":
        return BatchStatus.PENDING;
      case "PROCESSING":
        return BatchStatus.PROCESSING;
      default:
        return BatchStatus.FAILED; // Default to FAILED for unknown statuses
    }
  }

  /**
   * Convert API response item to internal format
   */
  private convertApiItemToBatchDetailsItem(
    apiItem: BatchDetailsItemApi
  ): BatchDetailsItem {
    return {
      ...apiItem,
      status: apiItem.status,
    };
  }

  /**
   * Get batch details with filters
   */
  async getBatchDetails(
    params: BatchDetailsParams
  ): Promise<BatchDetailsResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.startDate) {
        queryParams.append("startDate", params.startDate);
      }
      if (params.endDate) {
        queryParams.append("endDate", params.endDate);
      }
      if (params.formName) {
        queryParams.append("formName", params.formName);
      }
      if (params.requestGroup) {
        queryParams.append("requestGroup", params.requestGroup);
      }
      if (params.status && params.status !== BatchStatus.ALL) {
        queryParams.append("status", params.status);
      }
      if (params.page) {
        queryParams.append("page", params.page.toString());
      }
      if (params.perPage) {
        queryParams.append("perPage", params.perPage.toString());
      }

      const url = `/batch_history/${params.batchId}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

      const response: AxiosResponse<BatchDetailsApiResponse> =
        await this.client.get(url);

      // Convert API response to internal format
      const convertedData: BatchDetailsItem[] = response.data.data.map(item =>
        this.convertApiItemToBatchDetailsItem(item)
      );

      return {
        success: response.data.success,
        data: convertedData,
        pagination: response.data.pagination,
        filters: response.data.filters,
        message: response.data.message,
      };
    } catch (error: any) {
      console.error("Error fetching batch details:", error);
      throw new Error(
        error.response?.data?.message || "バッチ詳細の取得に失敗しました"
      );
    }
  }

  /**
   * Format date for display
   */
  formatRequestDate(dateString: string | null): string {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  /**
   * Get status display text in Japanese
   */
  getStatusDisplayText(status: string | null): string {
    if (!status) return "-";
    const batchStatus = this.mapApiStatusToBatchStatus(status);
    return getBatchStatusDisplayText(batchStatus);
  }

  /**
   * Get status CSS class
   */
  getStatusClass(status: string | null): string {
    if (!status) return "status-unknown";
    const batchStatus = this.mapApiStatusToBatchStatus(status);
    return getBatchStatusClass(batchStatus);
  }

  /**
   * Get all error messages from items
   */
  getAllErrorMessages(items: BatchExecutionItemDetail[]): string[] {
    return items
      .filter(item => item.status === "FAILED" && item.errorMessage)
      .map(item => item.errorMessage!)
      .filter((message, index, array) => array.indexOf(message) === index); // Remove duplicates
  }

  /**
   * Format error messages for display
   */
  formatErrorMessages(errorMessages: string[]): string {
    if (errorMessages.length === 0) return "-";
    return errorMessages.join("\n");
  }

  /**
   * Check if error message is too long for inline display
   */
  isErrorMessageTooLong(errorMessage: string): boolean {
    return errorMessage.length > 100;
  }

  /**
   * Truncate error message for inline display
   */
  truncateErrorMessage(errorMessage: string, maxLength: number = 50): string {
    if (errorMessage.length <= maxLength) return errorMessage;
    return errorMessage.substring(0, maxLength) + "...";
  }
}

// Export singleton instance
export const batchHistoryDetailService = new BatchHistoryDetailService();
export default batchHistoryDetailService;
